import type { StoryCtx } from './ctx.js';
import { eventsOf, mainTimelineOf, periodsOf } from './timeline.js';
import { childrenOf } from './tree.js';

// ---------------------------------------------------------------------------
// Zeitraffer (PF 10, PLAN.md §18): Die Zeitlinien der Handlungsfläche zeigen die Zeit nicht mehr proportional, sondern berechnet aus den
// Zeiten der Szenen. Eine stückweise lineare Abbildung von der linearen Lage (Stelle auf dem Zeitstrahl, wie gespeichert) auf die gezeigte:
//  - übliche Abstände bleiben im Verhältnis (der mittlere Abstand zwischen zwei Szenen wird `WARP.gap` breit),
//  - enge Stellen werden gespreizt (mindestens `WARP.min`, damit sich Marken nicht überdecken; gleiche Zeit bleibt gleiche Stelle),
//  - Spannen, die viel länger sind als üblich (mehr als `WARP.factor` × der mittlere Abstand), werden gerafft: schmal, mit Beschriftung
//    („··· 3 Jahre ···“, siehe Oberfläche), wenn keine Szene darüber liegt.
// Gespeichert wird nichts davon: Lage und Zeit bleiben, wie sie sind; Ziehen rechnet über `toLinear` zurück.
// ---------------------------------------------------------------------------

/** Ein Stück der Achse: die lineare Spanne `a`…`b` wird `x0`…`x1` gezeigt. */
export interface WarpSegment {
  a: number;
  b: number;
  x0: number;
  x1: number;
  /** gerafft: viel länger als üblich, schmal gezeigt */
  compressed: boolean;
  /** keine Szene (Zeitraum) liegt darüber – eine geraffte leere Spanne wird beschriftet */
  empty: boolean;
}

export interface TimeWarp {
  /** in der Reihenfolge der Achse, lückenlos; leer: keine Raffung (linear) */
  segments: WarpSegment[];
  /** lineare Lage → gezeigte */
  toDisplay: (linear: number) => number;
  /** gezeigte Lage → lineare */
  toLinear: (display: number) => number;
}

/**
 * Maße des Zeitraffers (Einheiten der Fläche). Ein Abstand reicht mindestens für eine Vorschaukarte (256 breit) samt Luft, damit sich die
 * Karten benachbarter Szenen nicht überdecken – auch vor und hinter einer Raffung.
 */
export const WARP = {
  /** Breite des mittleren Abstands zwischen zwei Szenen */
  gap: 320,
  /** kleinster Abstand zweier Stellen mit verschiedener Zeit */
  min: 272,
  /** ab so viel mal dem mittleren Abstand wird eine Spanne gerafft */
  factor: 4,
  /** Breite einer Raffung: Grundbreite, Zuwachs je Verzehnfachung der Spanne und höchstens */
  raffung: 280,
  rafflog: 40,
  raffmax: 360,
} as const;

/** Keine Raffung: gezeigt wird die lineare Lage. */
export const IDENTITY_WARP: TimeWarp = { segments: [], toDisplay: (v) => v, toLinear: (v) => v };

export interface WarpInput {
  /** Stellen, die den Maßstab bestimmen (Szenen, Fähnchen) */
  points: readonly number[];
  /** weitere Stellen ohne Einfluss auf den Maßstab (Start und Ende der Zeitlinien) */
  frame?: readonly number[];
  /** Zeiträume [von, bis]: darüber ist eine Spanne nicht leer */
  covered?: readonly (readonly [number, number])[];
}

const sortedUnique = (values: readonly number[]): number[] => [...new Set(values.filter(Number.isFinite))].sort((a, b) => a - b);

/** Mittlerer (unterer Median) Abstand */
const medianOf = (values: number[]): number => [...values].sort((a, b) => a - b)[Math.floor((values.length - 1) / 2)]!;

/**
 * Zeitraffer aus den Stellen einer Achse (bzw. mehrerer gekoppelter, alle in denselben linearen Koordinaten). Weniger als zwei
 * verschiedene Szenenstellen: keine Raffung. Die erste Stelle bleibt, wo sie ist; jenseits der ersten bzw. letzten geht es im Maßstab
 * des angrenzenden Stücks weiter (Ziehen über das Ende hinaus).
 */
export function timeWarp(input: WarpInput): TimeWarp {
  const scene = sortedUnique(input.points);
  if (scene.length < 2) return IDENTITY_WARP;
  const gaps = scene.slice(1).map((p, i) => p - scene[i]!);
  const ref = medianOf(gaps);
  const scale = WARP.gap / ref;
  const all = sortedUnique([...scene, ...(input.frame ?? [])]);
  const covered = input.covered ?? [];
  const segments: WarpSegment[] = [];
  let x = all[0]!;
  for (let i = 0; i + 1 < all.length; i++) {
    const a = all[i]!;
    const b = all[i + 1]!;
    const g = b - a;
    const compressed = g > WARP.factor * ref;
    const w = compressed ? Math.min(WARP.raffmax, WARP.raffung + WARP.rafflog * Math.log10(g / (WARP.factor * ref))) : Math.max(WARP.min, g * scale);
    segments.push({ a, b, x0: x, x1: x + w, compressed, empty: !covered.some(([from, to]) => from <= a && to >= b) });
    x += w;
  }
  const first = segments[0]!;
  const last = segments[segments.length - 1]!;
  // jenseits der Enden geht es so weiter, wie das angrenzende Stück zeigt (eine Raffung am Ende verlängert schnell)
  const slope = (seg: WarpSegment): number => (seg.x1 - seg.x0) / (seg.b - seg.a);
  const [before, after] = [slope(first), slope(last)];
  /** das Stück, in dem `v` liegt (lineare Lage bis `b` bzw. gezeigte bis `x1`), binär gesucht */
  const find = (v: number, end: 'b' | 'x1'): WarpSegment => {
    let l = 0;
    let r = segments.length - 1;
    while (l < r) {
      const m = (l + r) >> 1;
      if (v > segments[m]![end]) l = m + 1;
      else r = m;
    }
    return segments[l]!;
  };
  return {
    segments,
    toDisplay: (v) => {
      if (v <= first.a) return first.x0 + (v - first.a) * before;
      if (v >= last.b) return last.x1 + (v - last.b) * after;
      const s = find(v, 'b');
      return s.x0 + ((v - s.a) / (s.b - s.a)) * (s.x1 - s.x0);
    },
    toLinear: (d) => {
      if (d <= first.x0) return first.a + (d - first.x0) / before;
      if (d >= last.x1) return last.b + (d - last.x1) / after;
      const s = find(d, 'x1');
      return s.a + ((d - s.x0) / (s.x1 - s.x0)) * (s.b - s.a);
    },
  };
}

/**
 * Zeitraffer je Zeitlinie der Geschichte, in den linearen Koordinaten der Fläche (Lage des Zeitstrahls + Stelle). Die Hauptzeitlinie und
 * die an sie gekoppelten teilen sich einen (sie zeigen dieselbe Zeit untereinander); jede freie Zeitlinie hat ihren eigenen.
 */
export function timelineWarps(ctx: StoryCtx): Map<string, TimeWarp> {
  const timelines = childrenOf(ctx, ctx.story.rootId).filter((n) => n.kind === 'timeline' && n.timeline);
  const main = mainTimelineOf(ctx);
  const groups = new Map<string, typeof timelines>();
  for (const tl of timelines) {
    const key = tl.id === main?.id || tl.timeline!.coupled ? (main?.id ?? tl.id) : tl.id;
    groups.set(key, [...(groups.get(key) ?? []), tl]);
  }
  const out = new Map<string, TimeWarp>();
  for (const group of groups.values()) {
    const points: number[] = [];
    const frame: number[] = [];
    const covered: [number, number][] = [];
    for (const tl of group) {
      const x = tl.x ?? 0;
      const t = tl.timeline!;
      frame.push(x + t.startPos, x + t.endPos);
      for (const f of t.flags) points.push(x + f.pos);
      for (const e of eventsOf(ctx, tl.id)) if (e.at !== null) points.push(x + e.at);
      for (const p of periodsOf(ctx, tl.id)) {
        if (p.from === null || p.to === null) continue;
        points.push(x + p.from, x + p.to);
        covered.push([x + p.from, x + p.to]);
      }
    }
    const warp = timeWarp({ points, frame, covered });
    for (const tl of group) out.set(tl.id, warp);
  }
  return out;
}
